import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import React, { ReactNode, useEffect } from 'react';
import { toast } from 'react-toastify';              // 👈 import toast
import { useAppDispatch } from '../Redux/Store/Store';
import { loadClientOptions } from '../Redux/Store/Slices/client';

export const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
});

export const AxiosInterceptor = ({ children }: { children: ReactNode }) => {
  const { getAccessTokenSilently, logout, isAuthenticated, user, isLoading } = useAuth0();
  const dispatch = useAppDispatch();

  /* ------------------------------------------------------------------ */
  /* 1. REQUEST interceptor – attach the Auth0 bearer token              */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const requestInterceptor = async (config: any) => {
      try {
        const token = await getAccessTokenSilently();
        if (token) config.headers['Authorization'] = `Bearer ${token}`;
      } catch (err) {
        console.error('Error obtaining access token:', err);
      }
      return config;
    };

    const id = axiosInstance.interceptors.request.use(requestInterceptor);
    return () => axiosInstance.interceptors.request.eject(id);
  }, [getAccessTokenSilently]);

  /* ------------------------------------------------------------------ */
  /* 2. RESPONSE interceptor – toast errors (and optionally logout on 401) */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    /* Pass-through on success */
    const onSuccess = (response: any) => response;

    /* Centralised error handler */
    const onError = (err: any) => {
      if (err.response) {
        const { status, data } = err.response;

        /* Prefer JSON { message } → plain text → generic */
        const message =
            data?.message ??                // { message: "…" }
            (typeof data === 'string' ? data : null) ??
            err.message ??
            'Unexpected error';

        toast.error(message);             // 👈 pop the toast

        /* (Optional) Logout or redirect on 401 */
        if (status === 99999) {
          logout({ logoutParams: { returnTo: window.location.origin } });
        }
      } else {
        toast.error('Network error – please check your connection');
      }
      return Promise.reject(err);         // keep chain intact
    };

    const id = axiosInstance.interceptors.response.use(onSuccess, onError);
    return () => axiosInstance.interceptors.response.eject(id);
  }, [logout]);

  /* ------------------------------------------------------------------ */
  /* 3. Keep client options in sync with Auth0 state                     */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated && user) dispatch(loadClientOptions());
  }, [isAuthenticated, user, isLoading, dispatch]);

  return <>{children}</>;
};
